"use client";

import { useRouter } from "next/navigation";
import { Tables } from "../../../database.types";
import { Button } from "@/components/ui/button";
import { Ellipsis } from "lucide-react";
import Image from "next/image";
import { useFormDetails } from "@/hooks/provider/use-form-details";
import { defaultAgentImage } from "@/core/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteForm } from "@/hooks/provider/use-delete-form";
import { useState } from "react";

export function SidebarFormItem(props: Tables<"form">) {
  const router = useRouter();
  const toForm = () => router.push(`/form/${props.id}/results`);

  return (
    <div
      className="px-3 py-[5px] flex-shrink-0 bg-white rounded-lg w-full text-gray-600 text-15m hover:bg-gray-100 duration-200 cursor-pointer active:text-blue-500 truncate"
      onClick={toForm}
    >
      {props.name}
    </div>
  );
}

export function NoFormItem() {
  const router = useRouter();
  const toCreateForm = () => router.push("/form/create");
  return (
    <div className="px-4 py-3 rounded-2xl w-full max-w-[932px] flex items-center gap-5 bg-gray-50">
      <div className="flex items-center gap-3 flex-grow">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 48 48"
          fill="none"
        >
          <path
            d="M28 4H12C10.9391 4 9.92172 4.42143 9.17157 5.17157C8.42143 5.92172 8 6.93913 8 8V40C8 41.0609 8.42143 42.0783 9.17157 42.8284C9.92172 43.5786 10.9391 44 12 44H36C37.0609 44 38.0783 43.5786 38.8284 42.8284C39.5786 42.0783 40 41.0609 40 40V16L28 4Z"
            fill="#E5E5E5"
          />
          <path
            d="M28 12V4L40 16H32C30.9391 16 29.9217 15.5786 29.1716 14.8284C28.4214 14.0783 28 13.0609 28 12Z"
            fill="#A3A3A3"
          />
        </svg>
        <div className="flex flex-col gap-1">
          <div className="text-gray-600 text-17m">
            버튼을 눌러 첫 폼을 만들어보세요 →
          </div>
          <div className="text-gray-500 text-14r">아직 폼이 없어요.</div>
        </div>
      </div>
      <Button id="create-form-button" onClick={toCreateForm}>
        새 폼 만들기
      </Button>
    </div>
  );
}

export function MainFormItem(props: Tables<"form">) {
  const { submissions } = useFormDetails(props.id);
  const router = useRouter();
  const toForm = () => router.push(`/form/${props.id}/results`);
  const [dialog, setDialog] = useState(false);
  const { deleteForm } = useDeleteForm();

  const handleDelete = async () => {
    await deleteForm(props.id);
    setDialog(false);
  };

  const openDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDialog(true);
  };

  return (
    <>
      <div
        className="px-4 py-3 rounded-2xl w-full max-w-[932px] flex items-center gap-5 hover:bg-gray-100 duration-200 cursor-pointer"
        onClick={toForm}
      >
        <div className="flex items-center gap-3 flex-grow">
          <Image
            src={props.agent_image ? props.agent_image : defaultAgentImage}
            alt="Main 챗폼 Image"
            width={48}
            height={48}
            className="rounded-full border-[3px] min-w-12 min-h-12 max-w-12 max-h-12 object-cover aspect-square"
            style={{
              borderColor: props.color,
            }}
            unoptimized
          />
          <div className="flex flex-col gap-1">
            <div className="text-gray-600 text-17m">{props.name}</div>
            <div className="text-gray-500 text-14r">
              {props.agent_name} ∙ {submissions.length}개 응답
            </div>
          </div>
        </div>
        <Ellipsis
          className="w-6 h-6 text-opacity-400 flex-shrink-0"
          onClick={openDialog}
        />
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              정말 {props?.name ?? "새"} 폼을 삭제할까요?
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>
            폼을 삭제하면 복구할 수 없어요. 정말로 삭제할까요?
          </DialogDescription>
          <DialogFooter>
            <Button onClick={() => setDialog(false)} variant="secondary">
              돌아가기
            </Button>
            <Button onClick={handleDelete} variant="destructive">
              삭제하기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
